import { Component, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentMockService } from '../services/assessment-mock.service';
import { AssessmentStatement, ScaleValue } from '../models/assessment.model';
import { ScaleOrientation } from '../shared/scale-picker/scale-picker.component';

const PREFS_KEY = 'fs_asmt_prefs_v1';
const PER_PAGE_OPTIONS = [1, 5, 10];

@Component({
  selector: 'app-assessment-questions',
  templateUrl: './assessment-questions.component.html',
  styleUrls: ['./assessment-questions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AssessmentQuestionsComponent implements OnInit {
  token!: string;
  statements: AssessmentStatement[] = [];
  page = 0;                       // current page (0-based)
  mode: 'question' | 'review' = 'question';
  /** Set when a question was reached from the review grid, so we can offer a way back. */
  cameFromReview = false;
  hintDismissed = false;
  animating = false;
  reducedMotion = false;

  // Participant-maintained display preferences (persisted to localStorage).
  orientation: ScaleOrientation = 'vertical';
  perPage = 1;
  readonly perPageOptions = PER_PAGE_OPTIONS;

  constructor(private route: ActivatedRoute, private router: Router, private svc: AssessmentMockService) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token')!;
    this.statements = this.svc.statements;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loadPrefs();
    const run = this.svc.getRun(this.token)!;
    const first = this.svc.firstUnansweredIndex(run);
    this.hintDismissed = first > 0;
    if (first >= this.statements.length) { this.toReview(); }
    else { this.page = Math.floor(first / this.perPage); }
  }

  // --- preferences ---
  private loadPrefs(): void {
    try {
      const p = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
      if (p.orientation === 'vertical' || p.orientation === 'horizontal') { this.orientation = p.orientation; }
      if (PER_PAGE_OPTIONS.includes(p.perPage)) { this.perPage = p.perPage; }
    } catch { /* keep defaults */ }
  }
  private savePrefs(): void {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ orientation: this.orientation, perPage: this.perPage }));
  }
  setOrientation(o: ScaleOrientation): void { this.orientation = o; this.savePrefs(); }
  setPerPage(n: number | string): void {
    const firstIdx = this.page * this.perPage;    // keep the first visible question stable
    this.perPage = Number(n);
    this.page = Math.floor(firstIdx / this.perPage);
    if (this.mode === 'review') { this.mode = 'question'; }
    this.savePrefs();
  }

  // --- derived ---
  get total(): number { return this.statements.length; }
  get totalPages(): number { return Math.ceil(this.total / this.perPage); }
  get pageStatements(): AssessmentStatement[] {
    const start = this.page * this.perPage;
    return this.statements.slice(start, start + this.perPage);
  }
  globalIndex(s: AssessmentStatement): number { return this.statements.indexOf(s); }
  answered(id: string): ScaleValue | null { return this.svc.getRun(this.token)!.answers[id] ?? null; }
  get answeredCount(): number { return Object.keys(this.svc.getRun(this.token)!.answers).length; }
  get toGo(): number { return this.total - this.answeredCount; }
  get progressPct(): number { return Math.round((this.answeredCount / this.total) * 100); }
  get pageAllAnswered(): boolean { return this.pageStatements.every(s => this.answered(s.id) != null); }
  get isLastPage(): boolean { return this.page >= this.totalPages - 1; }
  get singlePerPage(): boolean { return this.perPage === 1; }

  // --- answering ---
  select(statement: AssessmentStatement, value: ScaleValue): void {
    if (this.animating) { return; }
    this.svc.answer(this.token, statement.id, value);
    if (this.singlePerPage) { this.advanceOne(); }   // keep the smooth one-at-a-time flow
  }

  private advanceOne(): void {
    const finish = () => {
      if (this.page < this.totalPages - 1) { this.page++; this.focusFirst(); }
      else { this.toReview(); }
    };
    if (this.reducedMotion) { finish(); return; }
    this.animating = true;
    setTimeout(() => { this.animating = false; finish(); }, 250);
  }

  // --- navigation ---
  nextPage(): void {
    if (this.page < this.totalPages - 1) { this.page++; this.focusFirst(); }
    else { this.toReview(); }
  }
  prevPage(): void {
    if (this.mode === 'review') { this.mode = 'question'; this.page = this.totalPages - 1; }
    else if (this.page > 0) { this.page--; this.focusFirst(); }
  }

  /**
   * Jump to a question from the review grid. Remembers where we came from: without this the
   * only way back to review is Next-ing to the last page, so checking one answer mid-way
   * stranded you (31 clicks from question 5 of 36).
   */
  jumpTo(globalIdx: number): void {
    this.mode = 'question';
    this.cameFromReview = true;
    this.page = Math.floor(globalIdx / this.perPage);
    this.focusFirst();
  }

  toReview(): void {
    this.mode = 'review';
    this.cameFromReview = false;   // we're there; the shortcut has done its job
  }

  completeAssessment(): void {
    if (this.toGo > 0) { return; }
    this.svc.complete(this.token);
    this.router.navigate(['/assessments', this.token, 'thanks']);
  }

  private focusFirst(): void {
    setTimeout(() => (document.querySelector('.asmt-q__text') as HTMLElement | null)?.focus(), 0);
  }

  @HostListener('window:keydown', ['$event'])
  onKey(ev: KeyboardEvent): void {
    if (this.mode !== 'question') { return; }
    if (this.singlePerPage && ev.key >= '1' && ev.key <= '5') {
      // key 1 = top option (Always like me = 5) … key 5 = bottom (1)
      this.select(this.pageStatements[0], (6 - Number(ev.key)) as ScaleValue);
    } else if (ev.key === 'ArrowLeft') { this.prevPage(); }
    // No pageAllAnswered gate: the Next button never had one, so requiring it here just made
    // the key look broken on an unanswered question while the button beside it skipped freely.
    else if (ev.key === 'ArrowRight') { this.nextPage(); }
  }
}
